import { getMessaging, getToken, isSupported, onMessage } from "firebase/messaging";
import app from "./firebaseInit";

let messagingInstance = null;

const getMessagingInstance = () => {
  if (!messagingInstance) {
    messagingInstance = getMessaging(app);
  }
  return messagingInstance;
};

/**
 * Registers the FCM service worker, requests notification permission, and
 * returns a device push token — or null if unsupported, denied, or the
 * VAPID key isn't configured. Never throws; callers treat this as a
 * progressive enhancement, not a hard requirement.
 * @returns {Promise<string|null>}
 */
export async function initAdminMessaging() {
  try {
    if (!("serviceWorker" in navigator) || !(await isSupported().catch(() => false))) {
      return null;
    }

    const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
    if (!vapidKey) {
      console.warn("VITE_FIREBASE_VAPID_KEY is not set — push notifications are disabled.");
      return null;
    }

    const registration = await navigator.serviceWorker.register("/firebase-messaging-sw.js");
    const permission = await Notification.requestPermission();
    if (permission !== "granted") return null;

    const token = await getToken(getMessagingInstance(), {
      vapidKey,
      serviceWorkerRegistration: registration,
    });
    return token || null;
  } catch (error) {
    console.error("Failed to initialize push messaging:", error);
    return null;
  }
}

/**
 * Subscribes to FCM messages received while the app tab is in the foreground
 * (background messages are handled by the service worker instead). Safe to
 * call even when messaging was never initialized (unsupported browser, no
 * VAPID key) — becomes a no-op rather than throwing.
 * @param {(payload: any) => void} callback
 * @returns {() => void} unsubscribe function
 */
export function onForegroundAdminMessage(callback) {
  try {
    return onMessage(getMessagingInstance(), callback);
  } catch (error) {
    console.error("Failed to subscribe to foreground messages:", error);
    return () => {};
  }
}
